import React from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { getExpertiseCategories } from "~/lib/functions/expertise";
import { Heading } from "~/components/ui/heading";
import { NamedIcon } from "~/components/ui/named-icon";
import { CTASection } from "~/components/ui/cta-section";
import { Button } from "~/components/ui/button";

export const Route = createFileRoute("/_public/coaches/category/")({
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData({
      queryKey: ["expertiseCategories"],
      queryFn: () => getExpertiseCategories(),
    });
    return {};
  },
  head: () => ({
    title: "Coach Categories | Dream Coach Match",
    meta: [
      {
        name: "description",
        content:
          "Browse top coaching categories and find the perfect coach for your goals. Dream Coach Match connects you with certified, expert coaches for every need.",
      },
    ],
  }),
  component: CoachCategoriesPage,
});

function CoachCategoriesPage() {
  const { data: categories } = useQuery({
    queryKey: ["expertiseCategories"],
    queryFn: () => getExpertiseCategories(),
  });

  if (!categories) {
    return <div>Loading...</div>;
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-12">
      <Heading
        preHeadline="Dream Coach Match"
        headline="Explore Top Coaching Categories"
        subheadline="Find the perfect coach for your goals. Discover the benefits of coaching, explore our expert-vetted categories, and take the first step toward real transformation."
        isH1
      />
      <div className="mb-10 mt-6 flex justify-center">
        <Link to="/discovery">
          <Button size="lg" variant="fancy">
            Find your coach
          </Button>
        </Link>
      </div>
      <div className="mt-8 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
        {categories.map((cat) => (
          <Link
            key={cat.id}
            to="/coaches/category/$categorySlug"
            params={{ categorySlug: cat.slug }}
            className="focus:ring-brand-400 duration-600 ease-out-expo group relative flex flex-col items-center rounded-2xl border border-gray-100 bg-gray-50 bg-white p-8 shadow-sm transition-all hover:z-10 hover:scale-[1.03] hover:shadow-xl focus:outline-none focus:ring-2 active:scale-100"
            tabIndex={0}
            aria-label={`View ${cat.listingName || cat.name} coaches`}
          >
            <span className="absolute inset-0" aria-hidden="true"></span>
            <div className="bg-brand-50 group-hover:bg-brand-100 mb-4 flex h-16 w-16 items-center justify-center rounded-full transition-colors">
              <NamedIcon
                name={cat.icon || ""}
                className="text-brand-600 group-hover:text-brand-700 h-9 w-9 transition-colors"
              />
            </div>
            <div className="text-brand-900 group-hover:text-brand-700 font-heading mb-2 text-center text-xl font-bold tracking-tight">
              {cat.listingName || cat.name}
            </div>
            {cat.description && (
              <div className="flex min-h-[80px] items-center justify-center text-center text-base text-gray-600 group-hover:text-gray-800">
                {cat.description}
              </div>
            )}
            <span className="text-brand-200 pointer-events-none absolute right-4 top-4 opacity-0 transition-opacity group-hover:opacity-100">
              <svg
                width="24"
                height="24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="feather feather-arrow-right"
              >
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </span>
          </Link>
        ))}
      </div>
      <div className="mx-auto my-16 max-w-prose">
        <CTASection
          title="Not sure where to start?"
          description="Try our Coach Discovery Tool to get matched with the perfect coach for your needs. Answer a few questions and get personalized recommendations."
          link="/discovery"
          linkText="Start Discovery"
        />
      </div>
      <section className="prose prose-lg mx-auto my-20 max-w-3xl max-w-prose">
        <h2>Why Work With a Coach?</h2>
        <p>
          Coaching is a proven way to accelerate your growth, overcome
          obstacles, and achieve your most important goals. Whether you want to
          advance your career, improve your well-being, or develop new skills, a
          great coach provides the structure, support, and accountability you
          need to succeed. At Dream Coach Match, we connect you with certified,
          experienced coaches who are passionate about helping you thrive.
        </p>
        <h3>What Can a Coach Help You With?</h3>
        <ul>
          <li>Clarifying your vision and setting actionable goals</li>
          <li>Building confidence and resilience</li>
          <li>Improving work-life balance and reducing stress</li>
          <li>Developing leadership and communication skills</li>
          <li>Making career transitions or finding purpose</li>
          <li>Enhancing relationships and emotional intelligence</li>
          <li>And much more—there's a coach for every need</li>
        </ul>
        <h2>Why Dream Coach Match?</h2>
        <p>
          Dream Coach Match is the best place to find your coach because we do
          things differently:
        </p>
        <ul>
          <li>
            <strong>Curated, Verified Experts:</strong> Every coach is vetted
            for credentials, experience, and results.
          </li>
          <li>
            <strong>Personalized Matching:</strong> Our discovery tool uses your
            goals and preferences to recommend the best coaches for you.
          </li>
          <li>
            <strong>Transparent Reviews:</strong> Read real reviews from people
            like you.
          </li>
          <li>
            <strong>Easy Booking:</strong> Book sessions and communicate with
            your coach directly through our platform.
          </li>
          <li>
            <strong>Support at Every Step:</strong> Our team is here to help you
            find the right fit and get the most from your coaching journey.
          </li>
        </ul>
        <h3>How to Get Started</h3>
        <ol>
          <li>Browse categories to explore different coaching specialties.</li>
          <li>
            Read about each category and see which resonates with your needs.
          </li>
          <li>Click on a category to view top coaches and learn more.</li>
          <li>Or, use our Coach Discovery Tool for a personalized match.</li>
        </ol>
        <h2>Ready to Transform Your Life?</h2>
        <p>
          The right coach can make all the difference. Whether you're looking
          for career growth, personal development, or support through life's
          challenges, Dream Coach Match is your trusted partner. Start your
          journey today and see what's possible with the guidance of a
          world-class coach.
        </p>
      </section>
      <div className="my-16 flex flex-col items-center gap-6">
        <p className="text-brand-900 font-heading max-w-2xl text-center text-2xl font-light tracking-tight">
          You're one step away from finding the coach who will help you achieve
          your dreams. Don't wait—take action now!
        </p>
        <Link to="/discovery">
          <Button size="lg" variant="fancy">
            Try the Coach Discovery Tool
          </Button>
        </Link>
      </div>
    </div>
  );
}
